import { useEffect, useState, type FormEvent } from "react";
import {
  createProject,
  createTeam,
  deleteProject,
  deleteTeam,
  errorMessage,
  inviteToTeam,
  joinTeam,
  leaveTeam,
  moveProject,
  renameProject,
  renameTeam,
  respondToInvite,
  revokeInvite,
  setTeamOwner,
} from "../api";
import { ownersOf, type Invite, type Project, type Team } from "../types";
import CopyButton from "../components/CopyButton";
import { useDashboard } from "./Dashboard";

/**
 * Teams and their projects: the workspace's shape, edited in one place.
 *
 * Everything else in the dashboard is scoped *by* a project; this page is where teams
 * and projects come from. Joining is by invite code -- hand someone the code, they
 * paste it here -- because it needs no email lookup, no pending-invitation state, and
 * no way to enumerate teams you were not told about.
 *
 * Renaming and deleting a team, and granting ownership, are owner-only -- and a team
 * can have several owners, so ownership is a badge on the member list rather than a
 * fixed founder. The backend enforces all of it; the page just hides what a plain
 * member cannot do.
 */
export default function Teams() {
  const {
    user,
    teams,
    projects,
    project,
    selectProject,
    incomingInvites,
    teamInvites,
    notify,
  } = useDashboard();

  const [teamName, setTeamName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);

  async function answerInvite(invite: Invite, accept: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      await respondToInvite({ inviteId: invite.id, accept });
      notify(
        accept ? `You joined "${invite.teamName}".` : `Invitation to "${invite.teamName}" declined.`
      );
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function submitCreateTeam(e: FormEvent) {
    e.preventDefault();
    const name = teamName.trim();
    if (!name || busy) return;

    setBusy(true);
    try {
      const res = await createTeam({ name });
      setTeamName("");
      notify(`Team "${name}" created.`);
      // Land in the new team's first project: the next thing to do there is connect
      // something, and every page is scoped by project.
      selectProject(res.data.projectId);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function submitJoin(e: FormEvent) {
    e.preventDefault();
    const code = inviteCode.trim();
    if (!code || busy) return;

    setBusy(true);
    try {
      const res = await joinTeam({ code });
      setInviteCode("");
      notify(`You joined "${res.data.name}".`);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="section">
        <div className="section-head">
          <div>
            <h2>Teams</h2>
            <p className="section-sub">
              Everything else in the dashboard is scoped by a project, and projects come
              in two kinds: private ones nobody can join or even see, and team ones
              shared with a team's members. In a team project, team-level connections
              reach every member; personal ones — and your setup key — are always yours
              alone.
            </p>
          </div>
        </div>

        {/* Invitations waiting on this user's answer. First, because it is the only
            thing on the page someone else is waiting for -- and it appears here live
            the moment a teammate sends it. */}
        {incomingInvites.length > 0 && (
          <div className="invite-banner-list">
            {incomingInvites.map((invite) => (
              <div key={invite.id} className="invite-banner">
                <span className="invite-banner-text">
                  <strong>{invite.invitedByEmail}</strong> invited you to join{" "}
                  <strong>{invite.teamName}</strong>.
                </span>
                <span className="invite-banner-actions">
                  <button
                    className="btn btn-sm"
                    disabled={busy}
                    onClick={() => void answerInvite(invite, true)}
                  >
                    Accept
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={busy}
                    onClick={() => void answerInvite(invite, false)}
                  >
                    Decline
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="grid">
          {teams.map((team) =>
            // The personal team is not presented as a team at all: it is where private
            // projects live. No invite code, no members -- the backend refuses both.
            team.personal ? (
              <PrivateProjectsCard
                key={team.id}
                team={team}
                projects={projects.filter((p) => p.teamId === team.id)}
                currentProjectId={project?.id ?? null}
                busy={busy}
                setBusy={setBusy}
                onSelectProject={selectProject}
                notify={notify}
              />
            ) : (
              <TeamCard
                key={team.id}
                team={team}
                uid={user.uid}
                projects={projects.filter((p) => p.teamId === team.id)}
                invites={teamInvites.filter((i) => i.teamId === team.id)}
                currentProjectId={project?.id ?? null}
                busy={busy}
                setBusy={setBusy}
                onSelectProject={selectProject}
                notify={notify}
              />
            )
          )}
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <div>
            <h2>Start or join</h2>
            <p className="section-sub">
              Create a team to share connections with others, or paste an invite code a
              teammate shared with you.
            </p>
          </div>
        </div>

        <div className="grid">
          <article className="card">
            <header className="card-head">
              <div className="card-title">
                <h3>Create a team</h3>
                <p>You become its owner, and it starts with one project.</p>
              </div>
            </header>
            <div className="field">
              <label>Team name</label>
              <form className="field-row" onSubmit={submitCreateTeam}>
                <input
                  value={teamName}
                  placeholder="Acme Corp"
                  aria-label="Team name"
                  disabled={busy}
                  onChange={(e) => setTeamName(e.target.value)}
                />
                <button className="btn" type="submit" disabled={busy || !teamName.trim()}>
                  Create team
                </button>
              </form>
            </div>
          </article>

          <article className="card">
            <header className="card-head">
              <div className="card-title">
                <h3>Join a team</h3>
                <p>Ask a member for the invite code on their team card.</p>
              </div>
            </header>
            <div className="field">
              <label>Invite code</label>
              <form className="field-row" onSubmit={submitJoin}>
                <input
                  value={inviteCode}
                  placeholder="Paste an invite code"
                  aria-label="Invite code"
                  spellCheck={false}
                  autoComplete="off"
                  disabled={busy}
                  onChange={(e) => setInviteCode(e.target.value)}
                />
                <button className="btn" type="submit" disabled={busy || !inviteCode.trim()}>
                  Join
                </button>
              </form>
            </div>
          </article>
        </div>
      </section>
    </>
  );
}

/**
 * The home of the user's private projects. Deliberately not a team card: no invite
 * code, no member list, no leave or delete -- there is nobody to share with, and the
 * backend refuses joins and invites to the personal team outright.
 */
function PrivateProjectsCard({
  team,
  projects,
  currentProjectId,
  busy,
  setBusy,
  onSelectProject,
  notify,
}: {
  team: Team;
  projects: Project[];
  currentProjectId: string | null;
  busy: boolean;
  setBusy: (b: boolean) => void;
  onSelectProject: (projectId: string) => void;
  notify: (message: string, kind?: "ok" | "error") => void;
}) {
  return (
    <article className="card">
      <header className="card-head">
        <div className="card-title">
          <h3>Private projects</h3>
          <p>
            Yours alone — nobody can join them, and nothing connected in them is ever
            shared.
          </p>
        </div>
      </header>

      <ProjectsField
        teamId={team.id}
        projects={projects}
        currentProjectId={currentProjectId}
        busy={busy}
        setBusy={setBusy}
        onSelectProject={onSelectProject}
        notify={notify}
      />
    </article>
  );
}

/** A real (non-personal) team; the personal team renders as PrivateProjectsCard. */
function TeamCard({
  team,
  uid,
  projects,
  invites,
  currentProjectId,
  busy,
  setBusy,
  onSelectProject,
  notify,
}: {
  team: Team;
  uid: string;
  projects: Project[];
  /** This team's outstanding invitations, live. */
  invites: Invite[];
  currentProjectId: string | null;
  busy: boolean;
  setBusy: (b: boolean) => void;
  onSelectProject: (projectId: string) => void;
  notify: (message: string, kind?: "ok" | "error") => void;
}) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState("");

  const owners = ownersOf(team);
  const isOwner = owners.includes(uid);
  /** An owner may leave only when ownership survives them; a plain member always may. */
  const canLeave = !isOwner || owners.length > 1;

  /** Wrap a callable so every button gets the same busy/toast plumbing. */
  async function run(action: () => Promise<unknown>, doneMessage?: string) {
    setBusy(true);
    try {
      await action();
      if (doneMessage) notify(doneMessage);
      return true;
    } catch (err) {
      notify(errorMessage(err), "error");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function submitRename(e: FormEvent) {
    e.preventDefault();
    const name = draftName.trim();
    if (!name || name === team.name) {
      setRenaming(false);
      return;
    }
    if (await run(() => renameTeam({ teamId: team.id, name }), `Team renamed to "${name}".`)) {
      setRenaming(false);
    }
  }

  function handleDelete() {
    const summary =
      `Delete "${team.name}"?\n\n` +
      `This deletes its ${projects.length} project${projects.length === 1 ? "" : "s"}, ` +
      `every member's MCP server and setup key for them, and every connection and ` +
      `stored credential in them — for all ${team.memberUids.length} member` +
      `${team.memberUids.length === 1 ? "" : "s"}. It cannot be undone.`;
    if (!confirm(summary)) return;
    void run(() => deleteTeam({ teamId: team.id }), `Team "${team.name}" deleted.`);
  }

  function handleLeave() {
    if (
      !confirm(
        `Leave "${team.name}"?\n\nIts projects disappear from your dashboard and any ` +
          `MCP server you set up for them stops serving immediately.`
      )
    ) {
      return;
    }
    void run(() => leaveTeam({ teamId: team.id }), `You left "${team.name}".`);
  }

  /** What to call a member on screen: their email if known, "You" for the viewer. */
  function labelFor(memberUid: string): string {
    if (memberUid === uid) return "You";
    return team.memberEmails?.[memberUid] ?? `${memberUid.slice(0, 8)}…`;
  }

  return (
    <article className="card">
      <header className="card-head">
        <div className="card-title">
          {renaming ? (
            <form className="rename" onSubmit={submitRename}>
              <input
                autoFocus
                value={draftName}
                disabled={busy}
                aria-label="Team name"
                onChange={(e) => setDraftName(e.target.value)}
              />
              <button className="btn btn-sm" type="submit" disabled={busy}>
                Save
              </button>
              <button
                className="btn btn-subtle btn-sm"
                type="button"
                disabled={busy}
                onClick={() => setRenaming(false)}
              >
                Cancel
              </button>
            </form>
          ) : isOwner ? (
            // The name doubles as the rename affordance for owners -- it is the thing
            // being renamed, so it is also the thing you click. Same idiom as slugs.
            <button
              className="slug-btn"
              title="Rename this team"
              disabled={busy}
              onClick={() => {
                setDraftName(team.name);
                setRenaming(true);
              }}
            >
              <h3>{team.name}</h3>
            </button>
          ) : (
            <h3>{team.name}</h3>
          )}
          <p>
            {team.memberUids.length} member{team.memberUids.length === 1 ? "" : "s"}
            {isOwner ? " · you own this" : ""}
          </p>
        </div>
      </header>

      <div className="field">
        <label>Invite someone</label>
        <form
          className="field-row"
          onSubmit={(e) => {
            e.preventDefault();
            const email = inviteEmail.trim();
            if (!email || busy) return;
            void run(async () => {
              await inviteToTeam({ teamId: team.id, email });
              setInviteEmail("");
            }, `Invited ${email}. It appears in their dashboard the next time they sign in — or instantly, if they already are.`);
          }}
        >
          <input
            type="email"
            value={inviteEmail}
            placeholder="teammate@example.com"
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <button className="btn" type="submit" disabled={busy || !inviteEmail.trim()}>
            Invite
          </button>
        </form>

        {invites.length > 0 && (
          <div className="team-members">
            {invites.map((invite) => (
              <div key={invite.id} className="team-member">
                <span className="team-member-name" title={`Invited by ${invite.invitedByEmail}`}>
                  {invite.email}
                </span>
                <span className="pill pill-xs pill-muted">invited</span>
                <button
                  className="btn btn-subtle btn-sm"
                  disabled={busy}
                  title="Withdraw this invitation"
                  onClick={() =>
                    void run(
                      () => revokeInvite({ inviteId: invite.id }),
                      `Invitation to ${invite.email} withdrawn.`
                    )
                  }
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}

        <p className="field-note">
          They must sign in with that email; the invitation is waiting on their Teams
          page. Or share the code <code className="code-chip">{team.inviteCode}</code>
          <CopyButton value={team.inviteCode} label="Copy" subtle /> — anyone holding it
          can join without being invited.
        </p>
      </div>

      {/* The member list earns its place once there can be several owners: "who can
          delete this?" has to be answerable by looking, and granting ownership needs
          someone to point at. */}
      <div className="field">
        <label>Members</label>
        <div className="team-members">
          {team.memberUids.map((memberUid) => {
            const memberIsOwner = owners.includes(memberUid);
            return (
              <div key={memberUid} className="team-member">
                <span className="team-member-name">{labelFor(memberUid)}</span>
                {memberIsOwner && <span className="pill pill-xs">owner</span>}
                {isOwner && (
                  <button
                    className="btn btn-subtle btn-sm"
                    disabled={busy || (memberIsOwner && owners.length === 1)}
                    title={
                      memberIsOwner && owners.length === 1
                        ? "A team must have at least one owner"
                        : memberIsOwner
                          ? "Take ownership away"
                          : "Owners can rename or delete the team and manage owners"
                    }
                    onClick={() =>
                      void run(
                        () =>
                          setTeamOwner({
                            teamId: team.id,
                            memberUid,
                            owner: !memberIsOwner,
                          }),
                        memberIsOwner
                          ? `${labelFor(memberUid)} is no longer an owner.`
                          : `${labelFor(memberUid)} is now an owner.`
                      )
                    }
                  >
                    {memberIsOwner ? "Remove owner" : "Make owner"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ProjectsField
        teamId={team.id}
        projects={projects}
        currentProjectId={currentProjectId}
        busy={busy}
        setBusy={setBusy}
        onSelectProject={onSelectProject}
        notify={notify}
      />

      {(canLeave || isOwner) && (
        <footer className="card-foot">
          {canLeave && (
            <button className="btn btn-ghost btn-sm" onClick={handleLeave} disabled={busy}>
              Leave team
            </button>
          )}
          {isOwner && (
            <button
              className="btn btn-ghost btn-sm btn-danger"
              onClick={handleDelete}
              disabled={busy}
            >
              Delete team
            </button>
          )}
        </footer>
      )}
    </article>
  );
}

/** The project list and the add-project form, shared by both kinds of card. */
function ProjectsField({
  teamId,
  projects,
  currentProjectId,
  busy,
  setBusy,
  onSelectProject,
  notify,
}: {
  teamId: string;
  projects: Project[];
  currentProjectId: string | null;
  busy: boolean;
  setBusy: (b: boolean) => void;
  onSelectProject: (projectId: string) => void;
  notify: (message: string, kind?: "ok" | "error") => void;
}) {
  const { user, teams } = useDashboard();
  const [projectName, setProjectName] = useState("");

  /**
   * Moving is offered where the project *is*: this card. It opens a dialog, because
   * picking a project and a destination is a decision about one thing, taken once --
   * same reasoning as the tools dialog -- and a row of two selects grew the card for
   * something rarely used.
   *
   * Only an owner of this team may move a project out (the backend enforces the same).
   * Every project is offered, the default one included: handing a setup built alone to a
   * team is the whole point, and the project keeps its id, so nobody reinstalls anything.
   */
  const [moving, setMoving] = useState(false);

  /** The project whose name is being edited in place, if any. */
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");

  const team = teams.find((t) => t.id === teamId);
  const isOwner = team ? ownersOf(team).includes(user.uid) : false;
  const destinations = teams.filter((t) => t.id !== teamId);
  const offerMove = isOwner && projects.length > 0 && destinations.length > 0;

  async function submitCreateProject(e: FormEvent) {
    e.preventDefault();
    const name = projectName.trim();
    if (!name || busy) return;

    setBusy(true);
    try {
      const res = await createProject({ teamId, name });
      setProjectName("");
      notify(`Project "${name}" created.`);
      onSelectProject(res.data.projectId);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function submitRename(e: FormEvent) {
    e.preventDefault();
    const project = projects.find((p) => p.id === renamingId);
    const name = draftName.trim();
    if (!project || !name || name === project.name) {
      setRenamingId(null);
      return;
    }

    setBusy(true);
    try {
      await renameProject({ projectId: project.id, name });
      setRenamingId(null);
      notify(`Project renamed to "${name}".`);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteProject(project: Project) {
    if (
      !confirm(
        `Delete "${project.name}"?\n\n` +
          `This deletes every member's MCP server and setup key for it, and every ` +
          `connection and stored credential in it. Any client still pointed at one of ` +
          `those URLs stops working immediately. It cannot be undone.`
      )
    ) {
      return;
    }

    setBusy(true);
    try {
      await deleteProject({ projectId: project.id });
      notify(`Project "${project.name}" deleted.`);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function handleMove(projectId: string, destTeamId: string) {
    const project = projects.find((p) => p.id === projectId);
    if (!project || busy) return;

    setBusy(true);
    try {
      const res = await moveProject({ projectId, teamId: destTeamId });
      setMoving(false);
      // Follow it. Moving the *default* project matters here: the dashboard falls back
      // to whatever the personal team holds when nothing has been chosen, so without
      // this a user who never touched the switcher would land on the fresh private
      // project next time and think their setup had gone.
      onSelectProject(projectId);
      notify(`Moved "${project.name}" to "${res.data.teamName}".`);
    } catch (err) {
      // The dialog stays open: the message explains the block (an owner missing from
      // the destination, say), and the natural next step is picking differently.
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="field">
      <label>Projects</label>
      {/* A row per project rather than a row of chips, now that a project is something
          you can act on and not only switch to -- the same shape as the member list
          above it, for the same reason. The name is still the switch. */}
      <div className="team-projects">
        {projects.map((p) =>
          p.id === renamingId ? (
            <form key={p.id} className="rename team-project" onSubmit={submitRename}>
              <input
                autoFocus
                value={draftName}
                disabled={busy}
                aria-label="Project name"
                onChange={(e) => setDraftName(e.target.value)}
              />
              <button className="btn btn-sm" type="submit" disabled={busy}>
                Save
              </button>
              <button
                className="btn btn-subtle btn-sm"
                type="button"
                disabled={busy}
                onClick={() => setRenamingId(null)}
              >
                Cancel
              </button>
            </form>
          ) : (
            <div key={p.id} className="team-project">
              <button
                className={`btn btn-sm team-project-name ${
                  p.id === currentProjectId ? "" : "btn-subtle"
                }`}
                title={
                  p.id === currentProjectId
                    ? "The project currently shown on the other pages"
                    : "Switch the dashboard to this project"
                }
                disabled={busy}
                onClick={() => onSelectProject(p.id)}
              >
                {p.name}
              </button>

              {/* Renaming is a member-level action, same bar as adding a project. */}
              <button
                className="btn btn-subtle btn-sm"
                disabled={busy}
                title="Rename this project — its MCP servers keep their URLs and keys"
                onClick={() => {
                  setDraftName(p.name);
                  setRenamingId(p.id);
                }}
              >
                Rename
              </button>

              {/* Deleting reaches into every member's setup, so it is owner-only --
                  the backend enforces the same, this only hides what would be refused. */}
              {isOwner && (
                <button
                  // Subtle, not ghost: every other action in these rows is, and a
                  // destructive one has no business being the loudest control on the
                  // card. btn-danger colours it red on hover, where it belongs.
                  className="btn btn-subtle btn-sm btn-danger"
                  disabled={busy}
                  title="Delete this project, its servers and every credential in it"
                  onClick={() => void handleDeleteProject(p)}
                >
                  Delete
                </button>
              )}
            </div>
          )
        )}
      </div>

      <form className="field-row" onSubmit={submitCreateProject}>
        <input
          value={projectName}
          placeholder="New project name"
          disabled={busy}
          onChange={(e) => setProjectName(e.target.value)}
        />
        <button className="btn" type="submit" disabled={busy || !projectName.trim()}>
          Add project
        </button>
      </form>

      {offerMove && (
        <button
          className="btn btn-subtle btn-sm move-project-open"
          disabled={busy}
          title="Move one of this card's projects to another team, connections and all"
          onClick={() => setMoving(true)}
        >
          Move a project…
        </button>
      )}

      {moving && team && (
        <MoveProjectDialog
          fromName={teamLabel(team)}
          projects={projects}
          destinations={destinations}
          fromPrivate={Boolean(team.personal)}
          busy={busy}
          onMove={(projectId, destTeamId) => void handleMove(projectId, destTeamId)}
          onClose={() => setMoving(false)}
        />
      )}
    </div>
  );
}

/** What to call a team on screen: personal teams read as the private-projects card does. */
function teamLabel(t: Team): string {
  return t.personal ? "Private projects" : t.name;
}

/**
 * The move itself, as a dialog: which project, and where to. Same shape as the tools
 * dialog -- backdrop and Escape close it, the footer leads with the way backwards.
 * It stays open on a refused move, because the error explains what to change.
 */
function MoveProjectDialog({
  fromName,
  projects,
  destinations,
  /** Moving out of the personal team, i.e. making a private project a team's. */
  fromPrivate,
  busy,
  onMove,
  onClose,
}: {
  fromName: string;
  projects: Project[];
  destinations: Team[];
  fromPrivate: boolean;
  busy: boolean;
  onMove: (projectId: string, destTeamId: string) => void;
  onClose: () => void;
}) {
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [destTeamId, setDestTeamId] = useState(destinations[0]?.id ?? "");

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form
        className="modal modal-narrow"
        role="dialog"
        aria-modal="true"
        aria-label="Move a project"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (projectId && destTeamId) onMove(projectId, destTeamId);
        }}
      >
        <header className="modal-head">
          <div>
            <h2>Move a project</h2>
            <p className="modal-sub">Out of {fromName} — connections and all.</p>
          </div>
          <button
            className="modal-close"
            type="button"
            disabled={busy}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="modal-body">
          <div className="field">
            <label>Project</label>
            <select
              value={projectId}
              disabled={busy}
              onChange={(e) => setProjectId(e.target.value)}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Move to</label>
            <select
              value={destTeamId}
              disabled={busy}
              onChange={(e) => setDestTeamId(e.target.value)}
            >
              {destinations.map((t) => (
                <option key={t.id} value={t.id}>
                  {teamLabel(t)}
                </option>
              ))}
            </select>
          </div>

          <p className="wizard-note">
            Every member's MCP URL and setup key for it stay exactly as they are; who
            those servers serve follows the destination's members from the moment it
            moves.
            {fromPrivate &&
              " Accounts you connected privately stay private — the team gets the " +
                "project and a server each in it, never your credentials."}
          </p>
        </div>

        <footer className="modal-foot">
          <button className="btn btn-subtle" type="button" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button className="btn" type="submit" disabled={busy || !projectId || !destTeamId}>
            Move project
          </button>
        </footer>
      </form>
    </div>
  );
}
